// Question Bank Store Module - 문제은행 관리
import questionBankService from '@/services/questionBankService'

const state = {
  questionGroups: [],
  currentGroup: null,
  searchResults: [],
  filters: {
    skill: '',
    level: '',
    verified: null
  },
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  },
  statistics: {
    totalGroups: 0,
    totalQuestions: 0,
    verifiedCount: 0,
    unverifiedCount: 0
  }
}

const getters = {
  getQuestionGroups: (state) => state.questionGroups,
  getCurrentGroup: (state) => state.currentGroup,
  getSearchResults: (state) => state.searchResults,
  getFilters: (state) => state.filters,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  getTotalGroups: (state) => state.pagination.total,
  getCurrentPage: (state) => state.pagination.page,
  getItemsPerPage: (state) => state.pagination.limit,
  getStatistics: (state) => state.statistics,
  getFilteredGroups: (state) => {
    let groups = [...state.questionGroups]
    
    if (state.filters.skill) {
      groups = groups.filter(g => g.skill === state.filters.skill)
    }
    if (state.filters.level) {
      groups = groups.filter(g => 
        g.questions.some(q => q.level === state.filters.level)
      )
    }
    if (state.filters.verified !== null) {
      groups = groups.filter(g => g.verified === state.filters.verified)
    }
    
    return groups
  }
}

const mutations = {
  SET_QUESTION_GROUPS(state, groups) {
    state.questionGroups = groups
  },
  SET_CURRENT_GROUP(state, group) {
    state.currentGroup = group
  },
  SET_CURRENT_QUESTION(state, question) {
    state.currentQuestion = question
  },
  ADD_QUESTION_GROUP(state, group) {
    state.questionGroups.push(group)
  },
  UPDATE_QUESTION_GROUP(state, updatedGroup) {
    const index = state.questionGroups.findIndex(g => g.groupId === updatedGroup.groupId)
    if (index !== -1) {
      state.questionGroups.splice(index, 1, updatedGroup)
    }
  },
  DELETE_QUESTION_GROUP(state, groupId) {
    state.questionGroups = state.questionGroups.filter(g => g.groupId !== groupId)
  },
  SET_SEARCH_RESULTS(state, results) {
    state.searchResults = results
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  RESET_FILTERS(state) {
    state.filters = {
      skill: '',
      level: '',
      verified: null
    }
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  SET_CURRENT_PAGE(state, page) {
    state.pagination.page = page
  },
  SET_STATISTICS(state, statistics) {
    state.statistics = { ...state.statistics, ...statistics }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  ADD_QUESTION_TO_GROUP(state, { groupId, question }) {
    const group = state.questionGroups.find(g => g.groupId === groupId)
    if (group) {
      group.questions.push(question)
    }
  },
  UPDATE_QUESTION_IN_GROUP(state, { groupId, questionId, updatedQuestion }) {
    const group = state.questionGroups.find(g => g.groupId === groupId)
    if (group) {
      const index = group.questions.findIndex(q => q.questionId === questionId)
      if (index !== -1) {
        group.questions.splice(index, 1, updatedQuestion)
      }
    }
  },
  DELETE_QUESTION_FROM_GROUP(state, { groupId, questionId }) {
    const group = state.questionGroups.find(g => g.groupId === groupId)
    if (group) {
      group.questions = group.questions.filter(q => q.questionId !== questionId)
    }
  }
}

const actions = {
  async fetchQuestionGroups({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const result = await questionBankService.fetchQuestionGroups(params)

      commit('SET_QUESTION_GROUPS', result.items)
      commit('SET_PAGINATION', {
        page: result.page,
        limit: result.limit,
        total: result.total
      })
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching question groups:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchStatistics({ commit }) {
    try {
      const statistics = await questionBankService.fetchStatistics()
      commit('SET_STATISTICS', statistics)
      return statistics
    } catch (error) {
      console.error('Error fetching statistics:', error)
      throw error
    }
  },

  async fetchQuestionGroup({ commit }, groupId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const group = await questionBankService.fetchQuestionGroup(groupId)
      commit('SET_CURRENT_GROUP', group)
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error fetching question group:', error)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createQuestionGroup({ commit }, groupData) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const newGroup = await questionBankService.createQuestionGroup(groupData)
      commit('ADD_QUESTION_GROUP', newGroup)
      return newGroup
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error creating question group:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateQuestionGroup({ commit }, { groupId, groupData }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const updatedGroup = await questionBankService.updateQuestionGroup(groupId, groupData)
      commit('UPDATE_QUESTION_GROUP', updatedGroup)
      return updatedGroup
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error updating question group:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteQuestionGroup({ commit }, groupId) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      await questionBankService.deleteQuestionGroup(groupId)
      commit('DELETE_QUESTION_GROUP', groupId)
      return true
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error deleting question group:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async searchQuestions({ commit }, searchParams) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    
    try {
      const result = await questionBankService.searchQuestions(searchParams)
      commit('SET_SEARCH_RESULTS', result.items)
      return result
    } catch (error) {
      commit('SET_ERROR', error.message)
      console.error('Error searching questions:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setFilters({ commit }, filters) {
    commit('SET_FILTERS', filters)
  },

  resetFilters({ commit }) {
    commit('RESET_FILTERS')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}